import classNames from 'classnames';
import React, { CSSProperties } from 'react';
import Image from '@/components/atoms/image/image';
import SkeletonLoader from '@/components/atoms/skeleton-loader/skeleton-loader';
import Typography, {
  TypographyStyle,
} from '@/components/atoms/typography/typography';
import { getInitials } from '@/libraries/utils/get-initials';
import styles from './profile-picture.module.scss';

export interface ProfilePictureProps {
  /** The class name to apply */
  className?: string;
  /** The first name of the user used for initials fallback */
  firstName?: string;
  /** Whether the border should be dashed */
  hasDashedBorder?: boolean;
  /** Whether to show the profile image */
  hideImage?: boolean;
  /** The URL of the user's profile image */
  imageUrl?: string;
  /** The fontsize style used for the initials */
  initialsFontSize?: number;
  /** The typography style used for the initials */
  initialsTagStyle?: TypographyStyle;
  /** Whether the module is isLoading data */
  isLoading?: boolean;
  /** The last name of the user used for initials fallback */
  lastName?: string;
  /** The mode of the profile picture */
  mode?: 'navigationButton' | 'invitation' | 'regular' | 'userAvatar';
  /** The size of the picture in pixels */
  size?: number;
}

const ProfilePicture = ({
  className,
  firstName,
  hasDashedBorder = false,
  hideImage = false,
  imageUrl,
  initialsFontSize,
  initialsTagStyle = 'labelSmall',
  isLoading = false,
  lastName,
  mode = 'regular',
  size = 80,
}: ProfilePictureProps) => {
  const shouldRenderImage = !!imageUrl && !hideImage;
  const hasCustomFontSize = initialsFontSize !== undefined;

  const style = {
    '--profile-picture-component-height': size,
    '--profile-picture-component-width': size,
    ...(hasCustomFontSize && {
      '--profile-picture-initials-fontSize': initialsFontSize,
    }),
  } as CSSProperties;

  const renderContent = () => {
    if (shouldRenderImage) {
      if (isLoading) {
        return (
          <SkeletonLoader
            className={classNames(styles.image, styles.loader)}
            height="100%"
            width="100%"
          />
        );
      }

      return (
        <Image
          alt={`${firstName} ${lastName}`}
          className={styles.image}
          src={imageUrl}
          unoptimized
          width={size}
        />
      );
    }

    if (isLoading) {
      return (
        <SkeletonLoader
          className={classNames(styles.name, styles.loader)}
          height="28px"
          width="40px"
        />
      );
    }

    return (
      <Typography
        className={classNames({
          [styles.hasCustomFontSize]: hasCustomFontSize,
        })}
        tagStyle={initialsTagStyle}
      >
        {getInitials({ firstName: firstName || '', lastName: lastName || '' })}
      </Typography>
    );
  };

  return (
    <div
      className={classNames(styles.component, className, {
        [styles.hasImage]: shouldRenderImage,
        [styles.isRegular]: mode === 'regular',
        [styles.isUserAvatar]: mode === 'userAvatar',
        [styles.isInvitation]: mode === 'invitation',
        [styles.isNavigationButton]: mode === 'navigationButton',
        [styles.hasDashedBorder]: hasDashedBorder,
      })}
      style={style}
    >
      {renderContent()}
    </div>
  );
};

ProfilePicture.displayName = 'ProfilePicture';

export default ProfilePicture;
